/*
 * Minimal client for LM Studio's local server (OpenAI-compatible API).
 *
 * HARD-WIRED TO LOCALHOST: the assistant sends page data in bulk, so the
 * host can never be configured to point anywhere else — only the port.
 * LM Studio needs "Enable CORS" on in its server settings; Chrome may also
 * ask once to allow this site to access the local network.
 */

const PORT_KEY = 'lmstudio_port';
const MODEL_KEY = 'lmstudio_model';
export const DEFAULT_LM_STUDIO_PORT = 1234;

const safeGet = (key: string) => {
  try { return window.localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string) => {
  try { window.localStorage.setItem(key, value); } catch { /* private mode etc. */ }
};

export const getLmStudioPort = (): number => {
  const n = Number(safeGet(PORT_KEY));
  return Number.isInteger(n) && n > 0 && n < 65536 ? n : DEFAULT_LM_STUDIO_PORT;
};

export const setLmStudioPort = (port: number) => {
  if (Number.isInteger(port) && port > 0 && port < 65536) safeSet(PORT_KEY, String(port));
};

export const getPreferredModel = () => safeGet(MODEL_KEY);
export const setPreferredModel = (model: string) => safeSet(MODEL_KEY, model);

// Conversation history, kept across pages and reloads, per signed-in user,
// in this browser only (it can contain HQ data the user saw). Cleared by
// "New chat" and on sign-out.
const HISTORY_PREFIX = 'lmstudio_history:';
const MAX_SAVED_TURNS = 60;
const MAX_SAVED_CHARS = 400_000;

export interface SavedAssistantHistory {
  turns: unknown[];
  aliases: [string, string][];
}

export const loadAssistantHistory = (user: string): SavedAssistantHistory | null => {
  if (!user) return null;
  try {
    const parsed = JSON.parse(safeGet(`${HISTORY_PREFIX}${user.toLowerCase()}`) || 'null');
    return parsed && Array.isArray(parsed.turns) ? parsed : null;
  } catch {
    return null;
  }
};

export const saveAssistantHistory = (user: string, history: SavedAssistantHistory) => {
  if (!user) return;
  let turns = history.turns.slice(-MAX_SAVED_TURNS);
  let text = JSON.stringify({ ...history, turns });
  while (text.length > MAX_SAVED_CHARS && turns.length > 2) {
    turns = turns.slice(2); // drop the oldest question + answer
    text = JSON.stringify({ ...history, turns });
  }
  safeSet(`${HISTORY_PREFIX}${user.toLowerCase()}`, text);
};

export const clearAssistantHistory = (user?: string) => {
  try {
    if (user) {
      window.localStorage.removeItem(`${HISTORY_PREFIX}${user.toLowerCase()}`);
      return;
    }
    Object.keys(window.localStorage).filter((k) => k.startsWith(HISTORY_PREFIX))
      .forEach((k) => window.localStorage.removeItem(k));
  } catch { /* private mode etc. */ }
};

const baseUrl = () => `http://localhost:${getLmStudioPort()}`;

export interface LmStudioModel {
  id: string;
  /**
   * Context window the model is LOADED with (LM Studio REST API v0). Only
   * set when loaded: an unloaded model's reported max_context_length (128K,
   * even 10M) says nothing about the context LM Studio will load it with
   * (its default can be as small as 4K), and on overflow LM Studio may drop
   * the start of the prompt — i.e. the page data — without an error.
   */
  contextLength?: number;
  maxContextLength?: number;
  loaded?: boolean;
}

/** Lists models; throws if LM Studio isn't reachable (not running / CORS off). */
export const listLmStudioModels = async (): Promise<LmStudioModel[]> => {
  const res = await fetch(`${baseUrl()}/v1/models`, { method: 'GET' });
  if (!res.ok) throw new Error(`LM Studio responded ${res.status}`);
  const body = await res.json();
  let models: LmStudioModel[] = (body.data || []).map((m: { id: string }) => ({ id: m.id }));
  const nonChat = new Set<string>();
  // Best-effort: LM Studio's own REST API exposes type, load state and context.
  try {
    const v0 = await fetch(`${baseUrl()}/api/v0/models`);
    if (v0.ok) {
      const detail = await v0.json();
      (detail.data || []).forEach((d: {
        id: string, type?: string, state?: string, loaded_context_length?: number, max_context_length?: number,
      }) => {
        if (d.type === 'embeddings') nonChat.add(d.id);
        const m = models.find((x) => x.id === d.id);
        if (m) {
          m.loaded = d.state === 'loaded';
          m.contextLength = m.loaded ? d.loaded_context_length : undefined;
          m.maxContextLength = d.max_context_length;
        }
      });
    }
  } catch {
    // Older LM Studio — no v0 API; fall back to the name.
  }
  models = models.filter((m) => !nonChat.has(m.id) && !/embed/i.test(m.id));
  return models;
};

/** Content + whether any reasoning chunks arrived, from complete SSE lines. */
const parseSseLines = (lines: string[]): { content: string, thinking: boolean } => {
  let content = '';
  let thinking = false;
  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) return;
    const payload = trimmed.slice(5).trim();
    if (!payload || payload === '[DONE]') return;
    try {
      const delta = JSON.parse(payload).choices?.[0]?.delta || {};
      if (delta.reasoning_content || delta.reasoning) thinking = true;
      if (typeof delta.content === 'string') content += delta.content;
    } catch {
      // Partial / keep-alive line — ignore.
    }
  });
  return { content, thinking };
};

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * Stream a chat completion. Calls onDelta with each content chunk; reasoning
 * chunks (reasoning models) are reported via onThinking so the UI can show
 * progress without displaying them. Resolves with the full text.
 */
export const streamLmStudioChat = async (opts: {
  model: string,
  messages: ChatMessage[],
  signal?: AbortSignal,
  onDelta: (chunk: string) => void,
  onThinking?: () => void,
}): Promise<string> => {
  const res = await fetch(`${baseUrl()}/v1/chat/completions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: opts.model, messages: opts.messages, stream: true, temperature: 0.2,
    }),
    signal: opts.signal,
  });
  if (!res.ok || !res.body) {
    const detail = await res.text().catch(() => '');
    throw new Error(`LM Studio responded ${res.status}${detail ? `: ${detail.slice(0, 300)}` : ''}`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let full = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // eslint-disable-next-line no-await-in-loop
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    const { content, thinking } = parseSseLines(lines);
    if (thinking) opts.onThinking?.();
    if (content) {
      full += content;
      opts.onDelta(content);
    }
  }
  return full;
};

/** Hide <think>…</think> blocks some models emit inline in the content. */
export const stripThinking = (text: string) => text.replace(/<think>[\s\S]*?(<\/think>|$)/g, '').trimStart();
